import type { OrderLineItem, OrderLineItemMeta } from '../../graphql/queries/orders';
import { metaArrayToRecord } from '../orderLineItemMeta';
import { parseOpsServicesFromMeta } from '../opsServices';
import type {
  ItineraryItem,
  ServiceVoucherData,
  StayItem,
} from './types';

/** Minimal order shape for voucher mapping (list + details). */
export type VoucherOrderSource = {
  databaseId: number;
  orderNumber?: string;
  date?: string;
  billing?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  } | null;
  customer?: { email?: string | null } | null;
  metaData?: OrderLineItemMeta[] | Array<{ key: string; value: string }> | null;
  lineItems?: {
    nodes: Array<
      Pick<OrderLineItem, 'metaData'> & {
        product?: { node?: { name?: string } | null } | null;
      }
    >;
  } | null;
};

function guestName(order: VoucherOrderSource): string {
  const first = order.billing?.firstName;
  const last = order.billing?.lastName;
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return order.billing?.email || order.customer?.email || '';
}

function monthYearCode(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}${yy}`;
}

function parsePackageRooms(raw?: string | null): { total: number; twn: number; dbl: number; sgl: number } | null {
  if (!raw) return null;
  try {
    const pkg = JSON.parse(raw) as { rooms?: Array<{ adults?: number; children?: number }> };
    const rooms = pkg.rooms || [];
    if (!rooms.length) return null;
    let twn = 0;
    let dbl = 0;
    let sgl = 0;
    rooms.forEach((room) => {
      const adults = Number(room.adults || 0);
      const children = Number(room.children || 0);
      if (adults === 1 && children === 0) sgl += 1;
      else if (adults === 2 && children === 0) twn += 1;
      else dbl += 1;
    });
    return { total: rooms.length, twn, dbl, sgl };
  } catch {
    return null;
  }
}

/**
 * Map a WooCommerce/portal order into ServiceVoucherData for PDF export.
 * Missing values stay undefined so the generator prints "—".
 */
export function buildServiceVoucherData(order: VoucherOrderSource): ServiceVoucherData {
  const ops = parseOpsServicesFromMeta(order.metaData || undefined).filter(
    (s) => s.status !== 'cancelled'
  );
  const lineItems = order.lineItems?.nodes || [];

  let transferMeta: Record<string, string> = {};
  let hotelMeta: Record<string, string> = {};
  let tourMeta: Record<string, string> = {};

  lineItems.forEach((item) => {
    const name = item.product?.node?.name || '';
    const md = metaArrayToRecord(item.metaData);
    if (name.toLowerCase().includes('transfer') || md._tat_pickup_name) {
      transferMeta = md;
    }
    if (md._ngk_hotel_name || name === 'NGK Hotel Booking') {
      hotelMeta = md;
    }
    if (
      name === 'Tour Placeholder' ||
      (md['Tour Title'] && !md._ngk_hotel_name && !name.toLowerCase().includes('hotel'))
    ) {
      tourMeta = md;
    }
  });

  const primaryOps = ops[0];
  const code =
    ops.map((s) => s.fileNo).find((v) => !!v) ||
    undefined;
  const agency = ops.map((s) => s.agency).find((v) => !!v) || undefined;

  const paxFromOps = ops.reduce((max, s) => {
    if (typeof s.pax === 'number' && !Number.isNaN(s.pax)) return Math.max(max, s.pax);
    return max;
  }, 0);

  const adults =
    Number(transferMeta._tat_adults || tourMeta.Adults || 0) || undefined;
  const children =
    Number(transferMeta._tat_children || tourMeta.Children || 0) || undefined;

  const hotelName =
    hotelMeta._ngk_hotel_name ||
    ops.map((s) => s.hotel).find((v) => !!v) ||
    transferMeta['tat_fields_hotel_/_accomodation'] ||
    undefined;

  const stays: StayItem[] = [];
  if (hotelMeta._ngk_check_in || hotelMeta._ngk_check_out) {
    stays.push({
      label: 'Stay',
      checkIn: hotelMeta._ngk_check_in || undefined,
      checkOut: hotelMeta._ngk_check_out || undefined,
    });
  }

  // If ops has distinct arrival/departure dates and no hotel stay, still useful as stay window
  const arrival = ops.find((s) => s.serviceType === 'arrival');
  const departure = ops.find((s) => s.serviceType === 'departure');
  if (!stays.length && (arrival?.serviceDate || departure?.serviceDate)) {
    stays.push({
      label: 'Stay',
      checkIn: arrival?.serviceDate || undefined,
      checkOut: departure?.serviceDate || undefined,
    });
  }

  const roomBreakdown = parsePackageRooms(hotelMeta.packageInfo || null) || undefined;

  const itinerary: ItineraryItem[] = [];

  // Transfer row from line-item meta
  if (transferMeta._tat_pickup_name || transferMeta['tat_fields_flight_no.'] || transferMeta['tat_fields_flight_no']) {
    const flight =
      transferMeta['tat_fields_flight_no.'] || transferMeta['tat_fields_flight_no'] || '';
    const from = transferMeta.tat_fields_from || '';
    const to = transferMeta.tat_fields_to || '';
    const depTime = transferMeta.tat_fields_departure_time || '';
    const arrTime = transferMeta.tat_fields_arrival || '';
    const depDate = transferMeta.tat_fields_departure_date || '';
    const detailsParts = [
      flight ? `Flight ${flight}` : '',
      from || to ? `${from} → ${to}` : '',
      depTime || arrTime ? `Dep ${depTime || '—'} / Arr ${arrTime || '—'}` : '',
    ].filter(Boolean);
    itinerary.push({
      stay: stays[0]?.label || 'Services',
      date: depDate || undefined,
      title: `Transfer ${transferMeta._tat_pickup_name || ''} → ${transferMeta._tat_dropoff_name || ''}`.trim(),
      details: detailsParts.join('  ') || undefined,
    });
  }

  // Hotel check-in/out
  if (hotelMeta._ngk_check_in) {
    itinerary.push({
      stay: stays[0]?.label || 'Services',
      date: hotelMeta._ngk_check_in,
      title: `Hotel check-in — ${hotelName || 'Hotel'}`,
    });
  }

  // Ops services (tours / arrival / departure / etc.)
  ops.forEach((service) => {
    const flightBits = [
      service.flightNumber || '',
      service.from || service.to ? `${service.from || ''} → ${service.to || ''}` : '',
      service.flightTime || '',
    ]
      .filter(Boolean)
      .join('  ');
    const opsBits = [
      service.pickupTime ? `Pickup ${service.pickupTime}` : '',
      service.guide ? `Guide: ${service.guide}` : '',
      service.driver ? `Driver: ${service.driver}` : '',
      service.vehicle ? `Vehicle: ${service.vehicle}` : '',
    ]
      .filter(Boolean)
      .join('  ·  ');

    itinerary.push({
      stay:
        service.serviceType === 'arrival' || service.serviceType === 'departure'
          ? 'Arrival / Departure'
          : service.serviceType === 'tour'
            ? 'Tours'
            : 'Services',
      date: service.serviceDate || undefined,
      title: service.serviceName || service.serviceType,
      details: [flightBits, opsBits].filter(Boolean).join('\n') || undefined,
    });
  });

  if (hotelMeta._ngk_check_out) {
    itinerary.push({
      stay: stays[0]?.label || 'Services',
      date: hotelMeta._ngk_check_out,
      title: `Hotel check-out — ${hotelName || 'Hotel'}`,
    });
  }

  const tourTitle = tourMeta['Tour Title'];
  if (tourTitle && !ops.some((s) => s.serviceName === tourTitle)) {
    itinerary.push({
      stay: 'Tours',
      date: tourMeta['Booking Date'] || undefined,
      title: tourTitle,
    });
  }

  const guide =
    ops.map((s) => s.guide).find((v) => !!v) ||
    undefined;
  const driver = ops.map((s) => s.driver).find((v) => !!v) || undefined;

  const orderNo = String(order.orderNumber ?? order.databaseId);
  const refSuffix = monthYearCode(order.date);
  const ref = code
    ? refSuffix
      ? `${code} / ${refSuffix}`
      : code
    : undefined;

  const guest = guestName(order);
  const guestPhone = order.billing?.phone || undefined;

  return {
    ref,
    code: code || undefined,
    bookingNumber: orderNo,
    voucherDate: order.date ? order.date.slice(0, 10) : undefined,
    groupName: agency && guest ? `${agency} / ${guest}` : guest || agency || undefined,
    guestName: guest || undefined,
    paxTotal: paxFromOps || ((adults || 0) + (children || 0)) || undefined,
    adults: adults || undefined,
    children: children || undefined,
    hotelName: hotelName || undefined,
    stays: stays.length ? stays : undefined,
    checkIn: stays[0]?.checkIn,
    checkOut: stays[0]?.checkOut,
    roomBreakdown: roomBreakdown || undefined,
    exclusions: 'Tourism Dirham Fees – to be paid direct to hotel upon check in',
    itinerary: itinerary.length ? itinerary : undefined,
    contacts: {
      tourLeader: guestPhone || guest
        ? { name: guest || 'Guest', phone: guestPhone }
        : undefined,
      guide: guide ? { name: guide } : driver ? { name: driver } : undefined,
      emergency: [
        { name: 'Adel', phone: '+971 56 682 2699' },
        { name: 'Mamu', phone: '+971 52 932 0023' },
      ],
    },
    greetAndGo: {
      whatsappContacts: [
        guestPhone ? { name: guest || 'Guest', phone: guestPhone } : null,
        { name: 'Adel', phone: '+971 56 682 2699' },
        { name: 'Mamu', phone: '+971 52 932 0023' },
      ].filter(Boolean) as Array<{ name?: string; phone?: string }>,
    },
  };
}
